import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';
import { RCIcon, RCTooltip } from '@reverbdotcom/cadence/components';
import { HeartStraightIcon, HeartStraightFillIcon } from '@reverbdotcom/cadence/icons/react';

export interface RCFavoriteButtonProps {
  /** Toggles the indicator on. State is managed upstream from the button. */
  favorited?: boolean;

  /** Standard button event handler */
  onClick?: (e?: any) => void;

  /** Button size. Defaults to `small`. `medium` is used on the item page. `mini` is used by listing cards. */
  size?: 'medium' | 'small' | 'mini';

  /** Button style variants. Defaults to `elevated` styling. `muted` uses a muted button (for inline placement w/ other muted buttons). */
  variant?: 'elevated' | 'muted' | 'transparent';

  /** Also rarely used (e.g. when you're the owner of the listing in question) */
  disabled?: boolean;

  /** The text label for the button. Displayed via tooltip (optional) and aria-label. Defaults to 'Add to Favorites'. */
  label?: string;

  /** Optionally show a 'buttonLabel' tooltip. Defaults to false. */
  showTooltip?: boolean;
}

export const RCFavoriteButton = React.forwardRef<HTMLButtonElement, RCFavoriteButtonProps>(
  function RCFavoriteButton({
    favorited = false,
    onClick,
    size = 'small',
    variant = 'elevated',
    disabled = false,
    label = undefined,
    showTooltip = false,
  }: RCFavoriteButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>, ref) {
    const buttonLabel = label ? label : favorited ? I18n.t('cadence.RCFavoriteButton.remove') : I18n.t('cadence.RCFavoriteButton.add');
    const classes = classNames(
      'rc-favorite-button',
      {
        'rc-favorite-button--favorited': favorited,
        'rc-favorite-button--medium': size === 'medium',
        'rc-favorite-button--mini': size === 'mini',
        'rc-favorite-button--muted': variant === 'muted',
        'rc-favorite-button--transparent': variant === 'transparent',
        'rc-favorite-button--elevated': variant === 'elevated',
      },
    );

    const button = (
      <button
        ref={ref}
        className={classes}
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={buttonLabel}
      >
        <span className="rc-favorite-button__icon">
          <RCIcon svgComponent={favorited ? HeartStraightFillIcon : HeartStraightIcon} />
        </span>
      </button>
    );

    if (showTooltip) {
      return (
        <RCTooltip
          text={buttonLabel}
          style="buttonLabel"
          placement="bottom"
          visualOnly
        >
          {button}
        </RCTooltip>
      );
    }

    return button;
  },
);
